<template>
  <!-- Plan decision 13: `GET /identity/token` answered TERMS_REQUIRED. The
       person accepts here, inside the tenant's own pre-auth page, or signs out. -->
  <PreAuthPage>
    <div class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <span class="terms__badge"><v-icon size="20" color="primary">gavel</v-icon></span>
      <h1 class="sh-card__title">{{ t("auth.identity.terms.title") }}</h1>
      <p class="sh-card__subtitle">{{ t("auth.identity.terms.body") }}</p>
    </div>

    <a class="terms__document" data-test="terms-document" :href="documentUrl" target="_blank" rel="noopener noreferrer">
      <v-icon size="16">description</v-icon>
      <span>{{ t("auth.identity.terms.document") }}</span>
    </a>

    <p v-if="failed" class="sh-card__error" role="alert">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ t("auth.identity.terms.failed") }}</span>
    </p>

    <v-btn
      class="terms__accept"
      data-test="terms-accept"
      color="primary"
      size="large"
      variant="flat"
      block
      :loading="accepting"
      @click="onAccept"
      >{{ t("auth.identity.terms.accept") }}</v-btn>
    <v-btn
      class="terms__decline"
      data-test="terms-decline"
      variant="text"
      size="small"
      block
      :disabled="accepting"
      @click="session.declineTerms()"
      >{{ t("auth.identity.terms.decline") }}</v-btn>
  </PreAuthPage>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { termsHref } from "@/tenant/loginLinks";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";

/** The platform terms that version 2.0 names. Used only when the tenant has no terms document of its own. */
const PLATFORM_TERMS_URL = "https://cdn.metaship.ai/terms_and_conditions.pdf";

export default defineComponent({
  name: "AcceptTerms",
  components: { LogoTile, PreAuthPage },
  setup() {
    const { t } = useI18n();
    const session = useSessionStore();
    const tenantName = computed(() => session.tenant?.name || "Metaship");
    const branding = computed(() => session.tenant?.branding ?? null);
    const documentUrl = computed(() => termsHref(session.tenant?.loginPage) ?? PLATFORM_TERMS_URL);
    const accepting = ref(false);
    const failed = ref(false);

    async function onAccept(): Promise<void> {
      accepting.value = true;
      failed.value = false;
      try {
        await session.acceptTerms();
      } catch {
        // Still on this step (the acceptance did not save) → say so. A failure
        // after the save moved the session on, and that screen shows its own error.
        if (session.status === "terms-required") failed.value = true;
      } finally {
        accepting.value = false;
      }
    }

    return { t, session, tenantName, branding, documentUrl, accepting, failed, onAccept };
  },
});
</script>

<style scoped>
.terms__badge {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.14);
}
.terms__document {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  padding: 10px 12px;
  border: 1px solid var(--sh-hairline);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
}
.terms__document:hover {
  text-decoration: underline;
}
.terms__accept {
  font-weight: 600;
}
.terms__decline {
  margin-top: 6px;
  font-size: 12px;
  color: var(--sh-text-60);
}
</style>
